import Loading from '@components/v2/ui/Loading/Loading';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import SearchableMenuList from '@components/v2/ui/Menu/SearchableMenuList';
import Box from '@mui/material/Box';
import { memo, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ProductAvailabilityChip from '../../../../../components/Product/ProductAvailabilityChip';
import LangEnum from '../../../../../lang/LangEnum';
import { WithId } from '../../../../../types/Base.types';
import { OrderFormTestIds } from '../../../../../utils/testIds/OrderForm.testIds';
import { OrderProductsTestIds } from '../../../../../utils/testIds/OrderProducts.testIds';
import { AvailabilityEnum, DeliveryWindow } from '../../../../products/ProductTypes';
import useProductAvailabilities from '../../../../products/hooks/useProductAvailabilities';
import { useTranslation } from '../../../../translation/TranslationContext';
import { ordersRepository } from '../../../repositories/ordersRepository/orders.repository';
import { DeliveryWindowAvailability } from '../../add-product/deliveryWindowSelect.types';
import { HandleSelectItemNewDeliveryWindow } from '../types/useOrderProductItem.types';

type OrderProductDeliveryWindowsMenuProps = {
  productId?: string;
  productColorId?: string;
  anchorEl: null | HTMLElement;
  deliveryWindow?:
    | DeliveryWindow
    | {
        id: string;
        name: string;
      };
  availability: AvailabilityEnum;
  onClick: HandleSelectItemNewDeliveryWindow;
  updatePosition: () => void;
};

const OrderProductDeliveryWindowsMenu = ({
  anchorEl,
  productId,
  productColorId,
  deliveryWindow,
  availability,
  onClick,
  updatePosition,
}: OrderProductDeliveryWindowsMenuProps) => {
  const params = useParams<WithId>();
  const { translate } = useTranslation();

  const {
    getAvailability,
    availabilities,
    isLoading: isLoadingAvailabilities,
  } = useProductAvailabilities({
    productColorId,
    productId,
    orderId: params.id,
  });

  const [isLoadingDeliveryWindows, setIsLoadingDeliveryWindows] = useState(false);
  const [deliveryWindowsAvailabilities, setDeliveryWindowAvailabilities] = useState<
    DeliveryWindowAvailability[]
  >([]);

  const getDeliveryWindows = useCallback(async () => {
    if (!productId || !productColorId || !params.id) {
      return;
    }
    setIsLoadingDeliveryWindows(true);
    const response = await ordersRepository(params.id).getOrderProductDeliveryWindows(
      productId,
      productColorId,
    );
    setIsLoadingDeliveryWindows(false);

    if (response.ok && response.data) {
      const infinitePreOrdersResponse: DeliveryWindowAvailability[] = (
        response.data.infinitePreOrderDeliveryWindows ?? []
      ).map((dw) => ({
        availability: AvailabilityEnum.INFINITE_PRE_ORDER,
        deliveryWindow: dw,
      }));

      const materialPreOrdersResponse: DeliveryWindowAvailability[] = (
        response.data.materialPreOrderDeliveryWindows ?? []
      ).map((dw) => ({
        availability: AvailabilityEnum.MATERIAL_PRE_ORDER,
        deliveryWindow: dw,
      }));

      const futureStocksResponse: DeliveryWindowAvailability[] = (
        response.data.futureStockDeliveryWindows ?? []
      ).map((dw) => ({
        availability: AvailabilityEnum.FUTURE_STOCK,
        deliveryWindow: dw,
      }));

      setDeliveryWindowAvailabilities([
        ...infinitePreOrdersResponse,
        ...materialPreOrdersResponse,
        ...futureStocksResponse,
      ]);
    }
  }, [params.id, productColorId, productId]);

  const handleClickDeliveryWindow = useCallback(
    (item: DeliveryWindowAvailability) =>
      onClick({
        availability: item.availability,
        newDwAvailability: item,
        lastAvailability: availability,
      }),
    [availability, onClick],
  );

  const isMenuOpen = !!anchorEl;

  const isSelectedOption = (item: DeliveryWindowAvailability) =>
    item.deliveryWindow?.id === deliveryWindow?.id && item.availability === availability;

  const sortedDeliveryWindows = [...deliveryWindowsAvailabilities].sort(
    (first, second) => Number(isSelectedOption(second)) - Number(isSelectedOption(first)),
  );

  const isReadyForDeliverySelected = availability === AvailabilityEnum.READY_FOR_DELIVERY;

  useEffect(() => {
    if (isMenuOpen) {
      getDeliveryWindows();
      getAvailability();
    }
  }, [anchorEl, getDeliveryWindows, getAvailability]);

  useEffect(() => {
    if (isMenuOpen && !isLoadingDeliveryWindows) {
      updatePosition();
    }
  }, [isMenuOpen, isLoadingDeliveryWindows, isLoadingAvailabilities, updatePosition]);

  const readyForDeliveryOption: DeliveryWindowAvailability | undefined =
    !isLoadingAvailabilities && availabilities.includes(AvailabilityEnum.READY_FOR_DELIVERY)
      ? { availability: AvailabilityEnum.READY_FOR_DELIVERY }
      : undefined;

  const options = [
    ...(isReadyForDeliverySelected && readyForDeliveryOption ? [readyForDeliveryOption] : []),
    ...sortedDeliveryWindows,
    ...(!isReadyForDeliverySelected && readyForDeliveryOption ? [readyForDeliveryOption] : []),
  ];

  const isReadyForDeliveryOption = (item: DeliveryWindowAvailability) =>
    item.availability === AvailabilityEnum.READY_FOR_DELIVERY;

  const getOptionLabel = (item: DeliveryWindowAvailability) =>
    isReadyForDeliveryOption(item)
      ? translate(LangEnum.GLOBAL_READY_FOR_DELIVERY)
      : (item.deliveryWindow?.name ?? '');

  const handleClickOption = (item: DeliveryWindowAvailability) => {
    if (!isReadyForDeliveryOption(item)) {
      handleClickDeliveryWindow(item);
      return;
    }

    onClick({
      availability: AvailabilityEnum.READY_FOR_DELIVERY,
      lastAvailability: availability,
      newDwAvailability: undefined,
    });
  };

  if (isLoadingDeliveryWindows) {
    return (
      <Box
        sx={{
          height: 46,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Loading size={24} sx={{ color: 'inherit' }} />
      </Box>
    );
  }

  return (
    <SearchableMenuList
      items={options}
      testId={OrderProductsTestIds.DELIVERY_WINDOW_SEARCH}
      item={{
        getKey: (item) => `${item.deliveryWindow?.id}-${item.availability}`,
        getSearchValue: getOptionLabel,
        onSelect: handleClickOption,
        render: (item) => {
          const isReadyForDelivery = isReadyForDeliveryOption(item);

          return (
            <MenuItem
              testId={isReadyForDelivery ? undefined : OrderFormTestIds.DELIVERY_WINDOW_ITEM}
              selected={isReadyForDelivery ? isReadyForDeliverySelected : isSelectedOption(item)}
              onClick={() => handleClickOption(item)}
            >
              <ProductAvailabilityChip
                availability={item.availability}
                label={getOptionLabel(item)}
              />
            </MenuItem>
          );
        },
      }}
    />
  );
};

export default memo(OrderProductDeliveryWindowsMenu);
